/** Rows for the engagement multi-line chart (Recharts `data` prop). */
export type EngagementAnalyticsRow = {
  /** Short label for the X axis, e.g. "01 Mar" */
  label: string;
  /** Tooltip header, e.g. "04.01.26" */
  tooltipDate: string;
  connectionsSent: number;
  connectionsAccepted: number;
  messagesSent: number;
  messageReplies: number;
  profileVisits: number;
  pendingConnections: number;
};

/** Split a non-negative integer total across `parts` days (remainder on earliest days). */
export function splitIntegerAcrossDays(total: number, parts: number): number[] {
  if (parts <= 0) return [];
  if (total <= 0) return Array(parts).fill(0);
  const base = Math.floor(total / parts);
  const r = total % parts;
  return Array.from({ length: parts }, (_, i) => base + (i < r ? 1 : 0));
}

export type EngagementTotals = {
  connectionsSent: number;
  connectionsAccepted: number;
  messagesSent: number;
  messageReplies: number;
  profileVisits: number;
  pendingConnections: number;
};

/**
 * Builds daily rows for the chart. Campaign data has no per-day timestamps yet, so each
 * total is spread evenly across the selected window (honest aggregate, approximate daily split).
 */
export function buildEngagementAnalyticsChartData(
  range: "week" | "month" | "custom",
  totals: EngagementTotals,
  options?: { startDate?: Date | null; endDate?: Date | null }
): EngagementAnalyticsRow[] {
  const hasCustomDates =
    range === "custom" &&
    options?.startDate instanceof Date &&
    options?.endDate instanceof Date &&
    !Number.isNaN(options.startDate.getTime()) &&
    !Number.isNaN(options.endDate.getTime()) &&
    options.startDate.getTime() <= options.endDate.getTime();

  let days = range === "week" ? 7 : 30;
  if (hasCustomDates) {
    const start = new Date(options!.startDate!);
    const end = new Date(options!.endDate!);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    const diffMs = end.getTime() - start.getTime();
    const dayCount = Math.floor(diffMs / (24 * 60 * 60 * 1000)) + 1;
    days = Math.max(1, Math.min(dayCount, 120));
  }

  const connectionsSent = splitIntegerAcrossDays(totals.connectionsSent, days);
  const connectionsAccepted = splitIntegerAcrossDays(totals.connectionsAccepted, days);
  const messagesSent = splitIntegerAcrossDays(totals.messagesSent, days);
  const messageReplies = splitIntegerAcrossDays(totals.messageReplies, days);
  const profileVisits = splitIntegerAcrossDays(totals.profileVisits, days);
  const pendingConnections = splitIntegerAcrossDays(totals.pendingConnections, days);

  const rows: EngagementAnalyticsRow[] = [];
  const end = hasCustomDates ? new Date(options!.endDate!) : new Date();
  end.setHours(0, 0, 0, 0);
  const start = hasCustomDates ? new Date(options!.startDate!) : null;
  if (start) start.setHours(0, 0, 0, 0);

  for (let i = 0; i < days; i++) {
    const d = new Date(start ?? end);
    if (start) {
      d.setDate(start.getDate() + i);
    } else {
      d.setDate(end.getDate() - (days - 1 - i));
    }
    const label = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = String(d.getFullYear()).slice(-2);
    rows.push({
      label,
      tooltipDate: `${dd}.${mm}.${yy}`,
      connectionsSent: connectionsSent[i],
      connectionsAccepted: connectionsAccepted[i],
      messagesSent: messagesSent[i],
      messageReplies: messageReplies[i],
      profileVisits: profileVisits[i],
      pendingConnections: pendingConnections[i],
    });
  }
  return rows;
}

function csvEscape(cell: string | number): string {
  const s = String(cell);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/** Serialize chart rows to CSV (Excel-friendly UTF-8 with header row). */
export function engagementAnalyticsRowsToCsv(rows: EngagementAnalyticsRow[]): string {
  const headers = [
    "Date",
    "Connections sent",
    "Connections accepted",
    "Messages sent",
    "Message replies",
    "Profile visits",
    "Pending connections",
  ];
  const lines = [headers.map(csvEscape).join(",")];
  for (const row of rows) {
    lines.push(
      [
        row.tooltipDate,
        row.connectionsSent,
        row.connectionsAccepted,
        row.messagesSent,
        row.messageReplies,
        row.profileVisits,
        row.pendingConnections,
      ]
        .map(csvEscape)
        .join(",")
    );
  }
  return lines.join("\r\n");
}
